/**
 * The 8 trash archetypes (PLAN §1 "Enemy kit shape" / §5). Venom-cult theme, original names.
 * HP tiers and forces follow Altar of Fangs MDT data via TIER_HP; rates follow the WCL
 * per-NPC cast tables (bolts every 6–8 s, frontals every 10–15 s, CC every 12–20 s,
 * busters every 15–20 s).
 *
 * +encounters (docs/design/encounters.md §3): every archetype has a signature mechanic with a
 * tell and a counter, and key breakpoints (+7 / +10, Fortified) add teeth. Dungeon-wide kick
 * priority (EnemyAbility.kickPriority): Serpent's Mending 1 › Ascension Rite 2 › Scale Ward 3 ›
 * Piercing Hiss 4 › Hypnotic Gaze 5 › Venom Bolt 6.
 */
import type { AuraDef, EffectSpec, EnemyAbility, NpcDef } from '@keystone/sim';
import { ENEMY, ENEMY_AURA, NPC } from '../ids';
import { npcMelee } from '../balance';
import { atKey, enemy, hit, keyHit, mech, pool, pulse, telegraph } from './common';

/** Warded allies can't be interrupted (Venom Priestess Scale Ward). */
const WARD_GATE = { interruptibleWhen: { casterLacksAura: ENEMY_AURA.scaleWard } } as const;

// ---------------------------------------------------------------------------
// Abilities
// ---------------------------------------------------------------------------

const rendFlesh = enemy({
  id: ENEMY.rendFlesh,
  name: 'Rend Flesh',
  school: 'physical',
  kind: 'tankbuster',
  description: 'Heavy hit on the tank plus a bleed.',
  cast: { type: 'cast', timeMs: 1500 },
  range: 8,
  timer: { first: 7000, every: 17000, jitter: 2000 },
  target: 'victim',
  counter: ['defensive'],
  effects: [
    hit('physical', 10000),
    {
      type: 'applyAura',
      aura: {
        id: ENEMY_AURA.lacerate,
        name: 'Lacerate',
        kind: 'debuff',
        durationMs: 6000,
        stacking: 'refresh',
        periodic: { intervalMs: 1000, effects: [hit('physical', 500, { avoidable: false })] },
      },
    },
  ],
});

const hemorrhage: AuraDef = {
  id: ENEMY_AURA.hemorrhage,
  name: 'Hemorrhage',
  kind: 'debuff',
  durationMs: 6000,
  stacking: 'stack',
  maxStacks: 8,
  periodic: { intervalMs: 1000, effects: [hit('physical', 300, { avoidable: false })] },
};
const frenzyMods = [
  { stat: 'damageDonePct' as const, value: 0.3 },
  { stat: 'hastePct' as const, value: 0.3 },
];
const bloodFrenzy = enemy({
  id: ENEMY.bloodFrenzy,
  name: 'Blood Frenzy',
  school: 'physical',
  kind: 'buff',
  description: 'At 30% health (40% Fortified) the Reaver frenzies: +30% damage and attack speed. +7: its swings stack Hemorrhage.',
  timer: { first: 0, every: 1000 },
  target: 'self',
  once: true,
  hpWindow: { max: 0.3 },
  hpWindowAffix: { fortified: { max: 0.4 } },
  priority: 20,
  counter: ['killAdd', 'defensive'],
  warning: { text: 'FRENZY', severity: 'danger' },
  effects: [
    atKey(
      7,
      [
        {
          type: 'applyAura',
          to: 'self',
          aura: {
            id: ENEMY_AURA.bloodFrenzyEmpowered,
            name: 'Blood Frenzy',
            kind: 'buff',
            durationMs: 0,
            stacking: 'ignore',
            dispelType: 'enrage',
            mods: frenzyMods,
            procs: [{ on: 'meleeSwing', effects: [{ type: 'applyAura', aura: hemorrhage }] }],
          },
        },
      ],
      [{ type: 'applyAura', to: 'self', aura: { id: ENEMY_AURA.bloodFrenzy, name: 'Blood Frenzy', kind: 'buff', durationMs: 0, stacking: 'ignore', dispelType: 'enrage', mods: frenzyMods } }],
    ),
  ],
});

const envenom: AuraDef = {
  id: ENEMY_AURA.envenom,
  name: 'Envenom',
  kind: 'debuff',
  durationMs: 6000,
  stacking: 'stack',
  maxStacks: 3,
  dispelType: 'poison',
  periodic: { intervalMs: 1000, effects: [hit('nature', 500, { avoidable: false })] },
};
const ascended: AuraDef = { id: ENEMY_AURA.ascended, name: 'Ascended', kind: 'buff', durationMs: 0, stacking: 'ignore', dispelType: 'magic' };

const venomBolt = enemy({
  id: ENEMY.venomBolt,
  name: 'Venom Bolt',
  school: 'nature',
  kind: 'bolt',
  description: 'Interruptible bolt at a random player plus a stacking poison. Once Ascended, it hits the whole party.',
  cast: { type: 'cast', timeMs: 2000 },
  interruptible: true,
  ...WARD_GATE,
  timer: { first: 2000, every: 7000, jitter: 1000 },
  target: 'random',
  priority: 1,
  kickPriority: 6,
  dispellable: 'poison',
  counter: ['interrupt', 'dispel'],
  effects: [
    {
      type: 'conditional',
      cond: { type: 'selfHasAura', auraId: ENEMY_AURA.ascended },
      then: [pulse('nature', 2150)],
      else: [keyHit('nature', 6000, 1.35), { type: 'applyAura', aura: envenom }],
    },
  ],
});

const ascensionRite = enemy({
  id: ENEMY.ascensionRite,
  name: 'Ascension Rite',
  school: 'shadow',
  kind: 'buff',
  description: 'At 60% health: a 4 s channel. If it finishes the Adept Ascends and Venom Bolt hits the whole party. Kick it!',
  cast: { type: 'channel', durationMs: 4000, ticks: 1 },
  interruptible: true,
  ...WARD_GATE,
  timer: { first: 0, every: 1000 },
  target: 'self',
  once: true,
  hpWindow: { max: 0.6 },
  priority: 15,
  kickPriority: 2,
  counter: ['interrupt'],
  warning: { text: 'Ascension Rite', severity: 'danger' },
  // +7: Ascended also hastes the Adept.
  effects: [
    { type: 'applyAura', to: 'self', aura: ascended },
    atKey(7, [{ type: 'applyAura', to: 'self', aura: { id: `${ENEMY_AURA.ascended}_haste`, name: 'Ascended Haste', kind: 'buff', durationMs: 0, stacking: 'ignore', mods: [{ stat: 'hastePct', value: 0.3 }] } }]),
  ],
});

/** +7: the Adept also opens the pull with a Rite (4 s in), so the kick rotation is tested immediately. */
const ascensionRiteEarly = enemy({
  ...ascensionRite,
  id: ENEMY.ascensionRiteEarly,
  description: '+7: the Adept opens the pull with an Ascension Rite (4 s channel). Kick it!',
  timer: { first: 4000, every: 1000 },
  hpWindow: undefined,
  minKeyLevel: 7,
});

const acidSpew = enemy({
  id: ENEMY.acidSpew,
  name: 'Acid Spew',
  school: 'nature',
  kind: 'frontal',
  description: 'A 25 yd cone toward a non-tank player. Leaves a poison. +10: an acid strip stays on the ground.',
  cast: { type: 'cast', timeMs: 1000 },
  timer: { first: 5000, every: 13000, jitter: 2000 },
  target: 'randomNotTank',
  shape: { type: 'cone', range: 25, angle: Math.PI / 2 },
  dispellable: 'poison',
  counter: ['dodge'],
  effects: [
    telegraph({ type: 'cone', range: 25, angle: Math.PI / 2 }, 'caster', 2000, [
      keyHit('nature', 12000, 1.4, { avoidable: false }),
      {
        type: 'applyAura',
        aura: {
          id: ENEMY_AURA.acidBurn,
          name: 'Acid Burn',
          kind: 'debuff',
          durationMs: 8000,
          stacking: 'refresh',
          dispelType: 'poison',
          periodic: { intervalMs: 1000, effects: [hit('nature', 800, { avoidable: false })] },
        },
      },
    ]),
    atKey(10, [{ ...pool('npc.serpent.acid_strip', 0, 10000, 2000, { anchor: 'caster', shape: { type: 'line', length: 22, width: 4 } }) }]),
  ],
});

const piercingHiss = enemy({
  id: ENEMY.piercingHiss,
  name: 'Piercing Hiss',
  school: 'nature',
  kind: 'cc',
  description: 'Interruptible. Party-wide hit and a 2 s silence. +7: also interrupts casts.',
  cast: { type: 'cast', timeMs: 2000 },
  interruptible: true,
  ...WARD_GATE,
  timer: { first: 9000, every: 18000, jitter: 2000 },
  target: 'none',
  priority: 2,
  kickPriority: 4,
  counter: ['interrupt'],
  effects: [
    pulse('nature', 1900),
    {
      type: 'applyAura',
      to: 'area',
      area: { anchor: 'caster', shape: { type: 'circle', radius: 40 }, affects: 'enemies' },
      aura: { id: ENEMY_AURA.piercingHiss, name: 'Piercing Hiss', kind: 'debuff', durationMs: 2000, stacking: 'refresh', cc: 'silence' },
    },
    atKey(7, [{ type: 'interrupt', to: 'area', area: { anchor: 'caster', shape: { type: 'circle', radius: 40 }, affects: 'enemies' }, lockoutMs: 3000 }]),
  ],
});

const hypnosis: AuraDef = { id: ENEMY_AURA.hypnosis, name: 'Hypnosis', kind: 'debuff', durationMs: 6000, stacking: 'unique', cc: 'disorient', dispelType: 'magic' };
const hypnoticGaze = enemy({
  id: ENEMY.hypnoticGaze,
  name: 'Hypnotic Gaze',
  school: 'shadow',
  kind: 'cc',
  description: 'Interruptible. Disorients a non-tank player for 6 s (Magic). +7: also the nearest ally within 8 yd.',
  cast: { type: 'cast', timeMs: 2500 },
  interruptible: true,
  ...WARD_GATE,
  timer: { first: 6000, every: 16000, jitter: 3000 },
  target: 'randomNotTank',
  dispellable: 'magic',
  priority: 2,
  kickPriority: 5,
  counter: ['interrupt', 'dispel', 'spread'],
  effects: [
    hit('shadow', 3000),
    { type: 'applyAura', aura: hypnosis },
    atKey(7, [{ type: 'applyAura', to: 'area', area: { anchor: 'target', shape: { type: 'circle', radius: 8 }, affects: 'enemies', excludePrimary: true, maxTargets: 1 }, aura: hypnosis }]),
  ],
});

const serpentsMending = enemy({
  id: ENEMY.serpentsMending,
  name: "Serpent's Mending",
  school: 'nature',
  kind: 'heal',
  description: 'Interruptible. Heals the most injured ally for 25% of its max health. Always kick.',
  cast: { type: 'cast', timeMs: 3000 },
  interruptible: true,
  ...WARD_GATE,
  timer: { first: 8000, every: 12000, jitter: 2000 },
  target: 'lowestHpAlly',
  priority: 10,
  kickPriority: 1,
  counter: ['interrupt'],
  effects: [{ type: 'heal', amount: { pctTargetMaxHp: 0.25 } }],
});

/** Absorb auras are referenced by id from `absorb`/shield effects, so they are registered standalone. */
const scaleWardAura: AuraDef = { id: ENEMY_AURA.scaleWard, name: 'Scale Ward', kind: 'buff', durationMs: 12000, stacking: 'unique', dispelType: 'magic', absorb: {} };
export const TRASH_AURAS: AuraDef[] = [scaleWardAura];

const scaleWard = enemy({
  id: ENEMY.scaleWard,
  name: 'Scale Ward',
  school: 'nature',
  kind: 'shield',
  description: 'Interruptible. Shields the most injured ally (~17% of its health; Fortified +25%, +7 more). A warded enemy cannot be interrupted until the shield breaks.',
  cast: { type: 'cast', timeMs: 1500 },
  interruptible: true,
  timer: { first: 12000, every: 20000, jitter: 2000 },
  target: 'lowestHpAlly',
  priority: 5,
  kickPriority: 3,
  counter: ['interrupt', 'burstShield'],
  effects: [
    mech({ kind: 'shield', on: 'target', aura: scaleWardAura, pctMaxHp: { base: 0.17, atKey: { 7: 0.24 }, affixMult: { fortified: 1.25 } } }),
    atKey(7, [{ type: 'applyAura', aura: { id: ENEMY_AURA.scaleWardHaste, name: 'Scale Ward', kind: 'buff', durationMs: 12000, stacking: 'refresh', mods: [{ stat: 'castTimePct', value: -0.3 }] } }]),
  ],
});

const crushingCoils = enemy({
  id: ENEMY.crushingCoils,
  name: 'Crushing Coils',
  school: 'physical',
  kind: 'tankbuster',
  description: 'Crushes the tank. +10: slows it by 50% for 4 s.',
  cast: { type: 'cast', timeMs: 2000 },
  range: 8,
  timer: { first: 9000, every: 20000, jitter: 2000 },
  target: 'victim',
  counter: ['defensive'],
  effects: [
    hit('physical', 13000),
    atKey(10, [{ type: 'applyAura', aura: { id: ENEMY_AURA.constricted, name: 'Constricted', kind: 'debuff', durationMs: 4000, stacking: 'refresh', mods: [{ stat: 'moveSpeedPct', value: -0.5 }] } }]),
  ],
});

/** Coagulating Venom: party-wide healing absorb (+7, or +4 with Fortified). */
const coagulate: EffectSpec = mech({
  kind: 'pick',
  rule: 'all',
  range: 60,
  effects: [
    mech({
      kind: 'healAbsorb',
      aura: { id: ENEMY_AURA.coagulatingVenom, name: 'Coagulating Venom', kind: 'debuff', durationMs: 15000, stacking: 'unique', healAbsorb: true },
      amount: { base: 6000 },
    }),
  ],
});
const venomNova = enemy({
  id: ENEMY.venomNova,
  name: 'Venom Nova',
  school: 'nature',
  kind: 'pulse',
  description: 'Unavoidable party-wide burst plus a poison (Poison). +7 (Fortified +4): also a 6k healing absorb on everyone.',
  cast: { type: 'cast', timeMs: 3000 },
  timer: { first: 14000, every: 22000, jitter: 2000 },
  target: 'none',
  dispellable: 'poison',
  counter: ['healerCd'],
  effects: [
    pulse('nature', 3500),
    {
      type: 'applyAura',
      to: 'area',
      area: { anchor: 'caster', shape: { type: 'circle', radius: 60 }, affects: 'enemies' },
      aura: {
        id: ENEMY_AURA.venomNova,
        name: 'Venom Nova',
        kind: 'debuff',
        durationMs: 6000,
        stacking: 'refresh',
        dispelType: 'poison',
        periodic: { intervalMs: 1000, effects: [hit('nature', 400, { avoidable: false })] },
      },
    },
    atKey(7, [coagulate], [mech({ kind: 'gate', minKey: 4, affix: 'fortified', effects: [coagulate] })]),
  ],
});

const tailLash = enemy({
  id: ENEMY.tailLash,
  name: 'Tail Lash',
  school: 'physical',
  kind: 'tankbuster',
  cast: { type: 'cast', timeMs: 1500 },
  range: 8,
  timer: { first: 6000, every: 16000, jitter: 2000 },
  target: 'victim',
  counter: ['defensive'],
  effects: [hit('physical', 12000)],
});

const petrifyStun = (id: string, extra: Partial<AuraDef> = {}): AuraDef => ({ id, name: 'Petrified', kind: 'debuff', durationMs: 4000, stacking: 'unique', cc: 'stun', dispelType: 'magic', ...extra });
const petrifyingGaze = enemy({
  id: ENEMY.petrifyingGlare,
  name: 'Petrifying Gaze',
  school: 'arcane',
  kind: 'cc',
  description: 'Turn away! Everyone facing the Basilisk when the cast ends takes damage and is petrified for 4 s (Magic). +10: petrified players take 50% more damage.',
  cast: { type: 'cast', timeMs: 2500 },
  timer: { first: 10000, every: 22000, jitter: 2000 },
  target: 'none',
  dispellable: 'magic',
  counter: ['lookAway'],
  warning: { text: 'Look away!', severity: 'danger' },
  effects: [
    mech({
      kind: 'gaze',
      range: 40,
      effects: [
        hit('arcane', 6000, { avoidable: false }),
        atKey(10, [{ type: 'applyAura', aura: petrifyStun(ENEMY_AURA.petrifiedBrittle, { mods: [{ stat: 'damageTakenPct', value: 0.5 }] }) }], [{ type: 'applyAura', aura: petrifyStun(ENEMY_AURA.petrified) }]),
      ],
    }),
  ],
});

const rainCircle = telegraph({ type: 'circle', radius: 6 }, 'target', 2500, [keyHit('nature', 10000, 1.45, { avoidable: false })]);
const venomRain = enemy({
  id: ENEMY.venomRain,
  name: 'Venom Rain',
  school: 'nature',
  kind: 'groundAoE',
  description: 'Orange rings under 2 random players (3 at +7). Spread out: overlapping rings stack. +10: each leaves a pool.',
  cast: { type: 'cast', timeMs: 1000 },
  timer: { first: 16000, every: 25000, jitter: 3000 },
  target: 'none',
  shape: { type: 'circle', radius: 6 },
  counter: ['spread', 'dodge'],
  effects: [
    mech({
      kind: 'pick',
      rule: 'random',
      count: { base: 2, atKey: { 7: 3 } },
      effects: [rainCircle, atKey(10, [pool('npc.basilisk.venom_rain_pool', 4, 10000, 2500, { anchor: 'target' })])],
    }),
  ],
});

export const TRASH_ABILITIES: EnemyAbility[] = [
  rendFlesh,
  bloodFrenzy,
  venomBolt,
  ascensionRite,
  ascensionRiteEarly,
  acidSpew,
  piercingHiss,
  hypnoticGaze,
  serpentsMending,
  scaleWard,
  crushingCoils,
  venomNova,
  tailLash,
  petrifyingGaze,
  venomRain,
];

// ---------------------------------------------------------------------------
// NPCs
// ---------------------------------------------------------------------------

const small = { radius: 0.8, combatReach: 1.2, moveSpeed: 7 };
const medium = { radius: 1.0, combatReach: 1.5, moveSpeed: 7 };
const large = { radius: 1.6, combatReach: 2.5, moveSpeed: 7 };

/** Septic Spatter: Venomspawn leave a poison puddle where they die (+7: longer, and it cuts healing). */
const septicSpatter: EffectSpec[] = [
  atKey(
    7,
    [
      pool('npc.venomspawn.spatter', 4, 8000, 2000, {
        anchor: 'caster',
        extra: [{ type: 'applyAura', aura: { id: ENEMY_AURA.septicSpatter, name: 'Septic', kind: 'debuff', durationMs: 4000, stacking: 'refresh', mods: [{ stat: 'healingTakenPct', value: -0.2 }] } }],
      }),
    ],
    [pool('npc.venomspawn.spatter', 4, 6000, 2000, { anchor: 'caster' })],
  ),
];

export const TRASH_NPCS: NpcDef[] = [
  {
    id: NPC.venomspawn,
    name: 'Venomspawn',
    tier: 'fodder',
    hpMult: 1.1, // harness +10 pacing (was 1.0)
    forces: 1,
    melee: npcMelee(450),
    abilities: [],
    onDeath: septicSpatter,
    ...small,
    color: '#7bd36a',
  },
  {
    id: NPC.fangReaver,
    name: 'Fang Reaver',
    tier: 'standard',
    hpMult: 1.1, // harness +10 pacing (was 1.0)
    forces: 5,
    melee: npcMelee(900),
    abilities: [ENEMY.rendFlesh, ENEMY.bloodFrenzy],
    ...medium,
    color: '#b5523b',
  },
  {
    id: NPC.cultAdept,
    name: 'Serpent-Cult Adept',
    tier: 'caster',
    hpMult: 1.1, // harness +10 pacing (was 1.0)
    forces: 7,
    melee: npcMelee(300),
    abilities: [ENEMY.venomBolt, ENEMY.ascensionRite, ENEMY.ascensionRiteEarly],
    ...medium,
    preferredRange: 30,
    color: '#9b59b6',
  },
  {
    id: NPC.spittingSerpent,
    name: 'Spitting Serpent',
    tier: 'caster',
    hpMult: 1.1, // harness +10 pacing (was 1.0)
    forces: 7,
    melee: npcMelee(600),
    abilities: [ENEMY.acidSpew, ENEMY.piercingHiss],
    ...medium,
    color: '#27ae60',
  },
  {
    id: NPC.coilMesmer,
    name: 'Coil Mesmerist',
    tier: 'caster',
    hpMult: 1.1, // harness +10 pacing (was 1.0)
    forces: 7,
    melee: npcMelee(300),
    abilities: [ENEMY.hypnoticGaze],
    ...medium,
    preferredRange: 30,
    color: '#e84393',
  },
  {
    id: NPC.venomPriestess,
    name: 'Venom Priestess',
    tier: 'caster',
    hpMult: 1.1, // harness +10 pacing (was 1.0)
    forces: 7,
    melee: npcMelee(300),
    abilities: [ENEMY.serpentsMending, ENEMY.scaleWard],
    ...medium,
    preferredRange: 25,
    color: '#00cec9',
  },
  {
    id: NPC.hoodedConstrictor,
    name: 'Hooded Constrictor',
    tier: 'big',
    hpMult: 1.1, // harness +10 pacing (was 1.0)
    forces: 25,
    melee: npcMelee(1400),
    abilities: [ENEMY.crushingCoils, ENEMY.venomNova],
    ...large,
    color: '#6c5ce7',
  },
  {
    id: NPC.elderBasilisk,
    name: 'Elder Basilisk',
    tier: 'miniboss',
    hpMult: 1.1, // harness +10 pacing (was 1.0)
    forces: 30,
    melee: npcMelee(1700),
    abilities: [ENEMY.tailLash, ENEMY.petrifyingGlare, ENEMY.venomRain],
    radius: 2.2,
    combatReach: 3.5,
    moveSpeed: 7,
    tauntDr: true,
    color: '#a1887f',
  },
  {
    // Not part of the dungeon: a sturdy target for M0 tests and the training grounds.
    id: NPC.trainingDummy,
    name: 'Training Dummy',
    tier: 'boss',
    hpMult: 1.1, // harness +10 pacing (was 1.0)
    forces: 0,
    melee: npcMelee(1200),
    abilities: [],
    radius: 1.2,
    combatReach: 2,
    moveSpeed: 0,
    ccImmune: true,
    color: '#95a5a6',
  },
];
